import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import '../styles/pages/userManagement.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const ROLE_LABELS = {
    admin: 'Administrador',
    receptionist: 'Recepción',
    vet: 'Veterinario',
    support: 'Soporte',
};

const ROLE_COLORS = {
    admin: 'role--admin',
    receptionist: 'role--receptionist',
    vet: 'role--vet',
    support: 'role--support',
};

function getInitials(name, username) {
    const src = name || username || '?';
    return src.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function UserManagement() {
    const { authorizedFetch } = useAuth();
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({ username: '', password: '', name: '', role: 'receptionist' });
    const [loading, setLoading] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [toast, setToast] = useState({ type: '', text: '' });

    useEffect(() => { fetchUsers(); }, []);

    useEffect(() => {
        if (!toast.text) return;
        const t = setTimeout(() => setToast({ type: '', text: '' }), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    const showToast = (type, text) => setToast({ type, text });

    const fetchUsers = async () => {
        try {
            const res = await authorizedFetch(`${API_URL}/api/users`);
            if (res.ok) setUsers(await res.json());
        } catch (e) {
            console.error('Error fetching users:', e);
        }
    };

    const handleInputChange = e =>
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = editingUser ? `${API_URL}/api/users/${editingUser._id}` : `${API_URL}/api/users`;
            const res = await authorizedFetch(url, {
                method: editingUser ? 'PUT' : 'POST',
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setFormData({ username: '', password: '', name: '', role: 'receptionist' });
                setEditingUser(null);
                fetchUsers();
                showToast('success', editingUser ? 'Usuario actualizado con éxito.' : 'Usuario creado con éxito.');
            } else {
                const err = await res.json().catch(() => ({}));
                showToast('error', err.error || 'Error al guardar usuario.');
            }
        } catch {
            showToast('error', 'Error al guardar usuario.');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({ username: user.username, password: '', name: user.name || '', role: user.role });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) return;
        try {
            const res = await authorizedFetch(`${API_URL}/api/users/${userId}`, { method: 'DELETE' });
            if (res.ok) {
                fetchUsers();
                showToast('success', 'Usuario eliminado.');
            } else {
                showToast('error', 'Error al eliminar usuario.');
            }
        } catch {
            showToast('error', 'Error al eliminar usuario.');
        }
    };

    const cancelEdit = () => {
        setEditingUser(null);
        setFormData({ username: '', password: '', name: '', role: 'receptionist' });
    };

    return (
        <div>
            <Navigation />
            <div className="um-container">

                {/* Header */}
                <div className="um-header">
                    <div>
                        <h1 className="um-title">Gestión de Usuarios</h1>
                        <p className="um-subtitle">Crea y administra los accesos del equipo.</p>
                    </div>
                    <div className="um-header-badge">{users.length} usuario{users.length !== 1 ? 's' : ''}</div>
                </div>

                {/* Toast */}
                {toast.text && (
                    <div className={`um-toast um-toast--${toast.type}`}>{toast.text}</div>
                )}

                <div className="um-layout">
                    {/* Form card */}
                    <div className="um-form-card">
                        <div className="um-form-header">
                            <span className="um-form-icon">{editingUser ? '✏️' : '➕'}</span>
                            <h2 className="um-form-title">{editingUser ? 'Editar usuario' : 'Nuevo usuario'}</h2>
                        </div>

                        <form onSubmit={handleSubmit} className="um-form">
                            <div className="um-field">
                                <label className="um-label">Usuario</label>
                                <input
                                    className="um-input"
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    required
                                    disabled={!!editingUser}
                                    placeholder="nombre_usuario"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="um-field">
                                <label className="um-label">
                                    Contraseña
                                    {editingUser && <span className="um-label-hint"> · dejar en blanco para no cambiar</span>}
                                </label>
                                <input
                                    className="um-input"
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required={!editingUser}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="um-field">
                                <label className="um-label">Nombre completo</label>
                                <input
                                    className="um-input"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Nombre Apellido"
                                />
                            </div>
                            <div className="um-field">
                                <label className="um-label">Rol</label>
                                <select
                                    className="um-select"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="receptionist">Recepción</option>
                                    <option value="admin">Administrador</option>
                                    <option value="vet">Veterinario</option>
                                    <option value="support">Soporte</option>
                                </select>
                            </div>

                            <div className="um-form-actions">
                                <button type="submit" className="um-btn-primary" disabled={loading}>
                                    {loading ? 'Guardando…' : editingUser ? 'Guardar cambios' : 'Crear usuario'}
                                </button>
                                {editingUser && (
                                    <button type="button" className="um-btn-ghost" onClick={cancelEdit}>
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* User list */}
                    <div className="um-list">
                        <p className="um-list-label">Equipo actual</p>
                        {users.length === 0 ? (
                            <p className="um-empty">No hay usuarios registrados.</p>
                        ) : (
                            users.map(user => (
                                <div key={user._id} className={`um-user-card${editingUser?._id === user._id ? ' um-user-card--editing' : ''}`}>
                                    <div className={`um-avatar ${ROLE_COLORS[user.role] || ''}`}>
                                        {getInitials(user.name, user.username)}
                                    </div>
                                    <div className="um-user-info">
                                        <span className="um-user-name">{user.name || user.username}</span>
                                        <span className="um-user-meta">@{user.username}</span>
                                    </div>
                                    <span className={`um-role-badge ${ROLE_COLORS[user.role] || ''}`}>
                                        {ROLE_LABELS[user.role] || user.role}
                                    </span>
                                    <div className="um-user-actions">
                                        <button className="um-btn-icon" onClick={() => handleEdit(user)} title="Editar">
                                            ✏️
                                        </button>
                                        <button className="um-btn-icon um-btn-icon--danger" onClick={() => handleDelete(user._id)} title="Eliminar">
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
